/** The end of the Academy Pass is near (3 days or less), today, or past: one-click renewal
 * (never an automatic charge, D-110). */

import { useAcademyAccountRetrieve } from "@agencyos/api-client";
import { daysLeft, formatXaf, localText, renewalStage } from "@agencyos/core";
import { buttonClass } from "@agencyos/ui-web";
import { CalendarClock } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

export function RenewalBanner() {
  const { t, i18n } = useTranslation();
  const account = useAcademyAccountRetrieve({ query: { retry: false, staleTime: 60_000 } });
  const data = account.data?.status === 200 ? account.data.data : undefined;
  if (!data) return null;
  const stage = renewalStage(data.active_until, data.can_renew);
  if (stage === "none" || stage === "active") return null;
  // D-142: a 7-day Pass ending: the 30-day Academy Pass is offered (never renewed alone)
  const upgrade = data.upgrade as { code: string; name: Record<string, string>; price_xaf: number; days: number } | null | undefined;
  if (upgrade && (stage === "soon" || stage === "last")) {
    return (
      <aside className={`as-renewal as-renewal--${stage}`} role="status" aria-live="polite">
        <CalendarClock aria-hidden size={20} />
        <p>
          <strong>{t("coaching.pass7.upgradeTitle")}</strong>{" "}
          <span className="ac-footnote">
            {t("coaching.pass7.upgradeText", {
              current: t("coaching.pass7.badge"),
              date: data.active_until ?? "",
              name: localText(upgrade.name, i18n.resolvedLanguage ?? "fr") || "Academy Pass",
              price: formatXaf(upgrade.price_xaf),
              days: upgrade.days,
            })}
          </span>
        </p>
        <Link className={buttonClass("primary", { size: "sm" })} to={`/academy/plans?plan=${encodeURIComponent(upgrade.code)}`}>
          {t("coaching.pass7.upgradeCta")}
        </Link>
      </aside>
    );
  }
  const text =
    stage === "soon"
      ? t("academySales.renewal.soon", { count: daysLeft(data.active_until ?? "") })
      : stage === "last"
        ? t("academySales.renewal.last")
        : t("academySales.renewal.expired");
  return (
    <aside className={`as-renewal as-renewal--${stage}`} role="status" aria-live="polite">
      <CalendarClock aria-hidden size={20} />
      <p>
        <strong>{text}</strong> <span className="ac-footnote">{t("academySales.renewal.noAuto")}</span>
      </p>
      <Link className={buttonClass("primary", { size: "sm" })} to="/academy/plans?renew=1">
        {t("academySales.renewal.cta")}
      </Link>
    </aside>
  );
}
